import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Euro,
  Users,
  Receipt,
  Calendar,
  Clock,
  Tag,
  Plus,
  Percent,
  UserPlus,
  User,
  CreditCard,
  Package,
  Truck,
  ShoppingBag,
  BarChart3,
  PieChart as PieChartIcon,
  Target,
  Phone,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Bell,
  MessageCircle,
  UserX,
  Sparkles,
  Mail,
  Star,
  Share2,
  Search,
  FileText,
  HelpCircle
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import ReportOperatori from "@/components/ReportOperatori";
import TrendAvanzati from "@/components/TrendAvanzati";
import BilancioMensile from "@/components/BilancioMensile";
import Fornitori from "@/pages/Fornitori";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
const GENDER_COLORS = { donna: '#ec4899', uomo: '#3b82f6', unisex: '#8b5cf6' };

const StatCard = ({ title, value, variazione, icon: Icon, prefix = "", suffix = "" }) => {
  const isPositive = variazione >= 0;
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-medium text-slate-500 truncate">{title}</p>
            <p className="text-2xl sm:text-3xl font-bold text-slate-800 mt-1 break-words">
              {prefix}{typeof value === 'number' ? value.toLocaleString('it-IT') : value}{suffix}
            </p>
            {variazione !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium ${
                isPositive ? 'text-emerald-600' : 'text-red-500'
              }`}>
                {isPositive ? <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                <span className="truncate">{isPositive ? '+' : ''}{variazione}% vs prec.</span>
              </div>
            )}
          </div>
          <div className="p-3 sm:p-4 bg-blue-50 rounded-xl shrink-0">
            <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Analisi() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'trend';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [periodo, setPeriodo] = useState("mese");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [clientiEta, setClientiEta] = useState([]);
  const [serviziTop, setServiziTop] = useState([]);
  const [andamento, setAndamento] = useState([]);
  const [orariPunta, setOrariPunta] = useState([]);
  const [promozioni, setPromozioni] = useState([]);
  const [serviziGenere, setServiziGenere] = useState(null);
  const [ficheGenere, setFicheGenere] = useState(null);
  const [clientiNuovi, setClientiNuovi] = useState(null);
  const [trendClientiNuovi, setTrendClientiNuovi] = useState([]);
  const [cardStats, setCardStats] = useState(null);
  const [prodottiStats, setProdottiStats] = useState(null);
  
  // Marketing state
  const [marketingDashboard, setMarketingDashboard] = useState(null);
  const [clientiDaRichiamare, setClientiDaRichiamare] = useState([]);
  const [giorniAssenza, setGiorniAssenza] = useState(30);
  const [loadingMarketing, setLoadingMarketing] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clienteAnalisi, setClienteAnalisi] = useState(null);
  const [clientiNuoviMarketing, setClientiNuoviMarketing] = useState(null); // Statistiche clienti nuovi con canale
  const [profilazioneStats, setProfilazioneStats] = useState(null); // Statistiche profilazione capelli

  useEffect(() => {
    fetchAllData();
  }, [periodo]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [
        overviewRes, etaRes, serviziRes, andamentoRes, orariRes, promoRes,
        serviziGenereRes, ficheGenereRes, clientiNuoviRes, trendClientiRes,
        cardStatsRes, prodottiStatsRes
      ] = await Promise.all([
        axios.get(`${API}/analytics/overview?periodo=${periodo}`, { headers }),
        axios.get(`${API}/analytics/clienti-eta`, { headers }),
        axios.get(`${API}/analytics/servizi-top?periodo=${periodo}&limit=5`, { headers }),
        axios.get(`${API}/analytics/andamento-giornaliero?giorni=${periodo === 'anno' ? 365 : periodo === 'mese' ? 30 : 7}`, { headers }),
        axios.get(`${API}/analytics/orari-punta?periodo=${periodo}`, { headers }),
        axios.get(`${API}/promozioni`, { headers }),
        axios.get(`${API}/analytics/servizi-genere?periodo=${periodo}`, { headers }),
        axios.get(`${API}/analytics/fiche-genere?periodo=${periodo}`, { headers }),
        axios.get(`${API}/analytics/clienti-nuovi?periodo=${periodo}`, { headers }),
        axios.get(`${API}/analytics/trend-clienti-nuovi?settimane=12`, { headers }),
        axios.get(`${API}/analytics/card-prepagate?periodo=${periodo}`, { headers }),
        axios.get(`${API}/analytics/prodotti-venduti?periodo=${periodo}`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setClientiEta(etaRes.data);
      setServiziTop(serviziRes.data);
      setAndamento(andamentoRes.data);
      setOrariPunta(orariRes.data);
      setPromozioni(promoRes.data);
      setServiziGenere(serviziGenereRes.data);
      setFicheGenere(ficheGenereRes.data);
      setClientiNuovi(clientiNuoviRes.data);
      setTrendClientiNuovi(trendClientiRes.data);
      setCardStats(cardStatsRes.data);
      setProdottiStats(prodottiStatsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle statistiche");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Marketing Data
  const fetchMarketingData = async () => {
    setLoadingMarketing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const meseCorrente = new Date().toISOString().slice(0, 7); // YYYY-MM
      const [dashboardRes, clientiRes, clientiNuoviRes, profilazioneRes] = await Promise.all([
        axios.get(`${API}/marketing/dashboard`, { headers }),
        axios.get(`${API}/marketing/clienti-da-richiamare?giorni_assenza=${giorniAssenza}&limite=50`, { headers }),
        axios.get(`${API}/marketing/clienti-nuovi?mese=${meseCorrente}`, { headers }),
        axios.get(`${API}/analytics/profilazione-capelli`, { headers })
      ]);
      setMarketingDashboard(dashboardRes.data);
      setClientiDaRichiamare(clientiRes.data.clienti || []);
      setClientiNuoviMarketing(clientiNuoviRes.data);
      setProfilazioneStats(profilazioneRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati marketing");
    } finally {
      setLoadingMarketing(false);
    }
  };

  // Fetch analisi singolo cliente
  const fetchAnalisiCliente = async (clienteId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/marketing/analisi-cliente/${clienteId}`, { headers });
      setClienteAnalisi(response.data);
      setSelectedCliente(clienteId);
    } catch (error) {
      toast.error("Errore nel caricamento analisi cliente");
    }
  };

  // Imposta reminder
  const handleImpostaReminder = async (cliente) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const oggi = new Date();
      oggi.setDate(oggi.getDate() + 7); // Default: tra 7 giorni
      const dataReminder = oggi.toISOString().split('T')[0];
      
      await axios.post(`${API}/marketing/imposta-reminder`, null, {
        headers,
        params: {
          cliente_id: cliente.cliente_id,
          data_reminder: dataReminder,
          nota: `Richiamare cliente - Assente da ${cliente.giorni_assenza} giorni`,
          servizio_suggerito: cliente.servizi_preferiti?.[0] || ''
        }
      });
      toast.success(`Reminder impostato per ${cliente.nome} ${cliente.cognome}`);
    } catch (error) {
      toast.error("Errore nell'impostazione del reminder");
    }
  };

  // Invia email di richiamo
  const handleInviaRichiamo = async (cliente) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/marketing/invia-richiamo`, null, {
        headers,
        params: {
          cliente_id: cliente.cliente_id
        }
      });
      if (response.data.success) {
        toast.success(`Email inviata a ${cliente.nome} ${cliente.cognome}`);
      } else {
        toast.error(response.data.error || "Errore nell'invio email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio email");
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-medium text-slate-800">{label}</p>
          {payload.map((entry) => (
            <p key={entry.dataKey || entry.name} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.name.includes('ncasso') ? '€' : ''}{entry.value.toLocaleString('it-IT')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analisi-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 break-words" style={{ fontFamily: 'Playfair Display, serif' }}>
            Analisi & Marketing
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">Monitora l'andamento del tuo salone</p>
        </div>
      </div>

      {/* Tabs Principali */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'marketing') fetchMarketingData(); }} className="w-full">
        <div className="-mx-2 sm:mx-0 overflow-x-auto scrollbar-hide mb-6">
          <TabsList className="inline-flex sm:grid sm:w-full sm:grid-cols-6 w-max sm:w-full gap-1 sm:gap-0 px-2 sm:px-0">
            <TabsTrigger value="trend" className="flex items-center gap-1.5 px-3 sm:px-2 whitespace-nowrap text-xs sm:text-sm">
              <TrendingUp className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Trend & KPI</span>
              <span className="sm:hidden">Trend</span>
            </TabsTrigger>
            <TabsTrigger value="bilancio" className="flex items-center gap-1.5 px-3 sm:px-2 whitespace-nowrap text-xs sm:text-sm" data-testid="bilancio-tab">
              <Euro className="w-4 h-4 shrink-0" />
              Bilancio
            </TabsTrigger>
            <TabsTrigger value="fornitori" className="flex items-center gap-1.5 px-3 sm:px-2 whitespace-nowrap text-xs sm:text-sm" data-testid="fornitori-tab">
              <Truck className="w-4 h-4 shrink-0" />
              Fornitori
            </TabsTrigger>
            <TabsTrigger value="dettaglio" className="flex items-center gap-1.5 px-3 sm:px-2 whitespace-nowrap text-xs sm:text-sm">
              <BarChart3 className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Analisi Dettaglio</span>
              <span className="sm:hidden">Dettaglio</span>
            </TabsTrigger>
            <TabsTrigger value="operatori" className="flex items-center gap-1.5 px-3 sm:px-2 whitespace-nowrap text-xs sm:text-sm">
              <Users className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Report Operatori</span>
              <span className="sm:hidden">Operatori</span>
            </TabsTrigger>
            <TabsTrigger value="marketing" className="flex items-center gap-1.5 px-3 sm:px-2 whitespace-nowrap text-xs sm:text-sm" data-testid="marketing-tab">
              <Target className="w-4 h-4 shrink-0" />
              Marketing
            </TabsTrigger>
          </TabsList>
        </div>

        {/* TAB: Trend Avanzati */}
        <TabsContent value="trend">
          <TrendAvanzati />
        </TabsContent>

        {/* TAB: Bilancio Mensile */}
        <TabsContent value="bilancio" data-testid="bilancio-tab-content">
          <BilancioMensile />
        </TabsContent>

        {/* TAB: Fornitori (embedded full page) */}
        <TabsContent value="fornitori" data-testid="fornitori-tab-content">
          <Fornitori />
        </TabsContent>

        {/* TAB: Analisi Dettaglio */}
        <TabsContent value="dettaglio">
          <div className="space-y-6">
            {/* Filtro Periodo */}
            <div className="flex justify-end">
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="settimana">Settimana</SelectItem>
                  <SelectItem value="mese">Mese</SelectItem>
                  <SelectItem value="anno">Anno</SelectItem>
                </SelectContent>
              </Select>
            </div>
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Incasso Totale"
            value={overview.incasso.corrente}
            variazione={overview.incasso.variazione}
            icon={Euro}
            prefix="€"
          />
          <StatCard
            title="Passaggi Cassa"
            value={overview.passaggi_cassa.corrente}
            variazione={overview.passaggi_cassa.variazione}
            icon={Calendar}
          />
          <StatCard
            title="Clienti Unici"
            value={overview.clienti_unici.corrente}
            variazione={overview.clienti_unici.variazione}
            icon={Users}
          />
          <StatCard
            title="N° Scontrini"
            value={overview.scontrini.corrente}
            variazione={overview.scontrini.variazione}
            icon={Receipt}
          />
          <StatCard
            title="Fiche Media"
            value={overview.fiche_media.corrente}
            variazione={overview.fiche_media.variazione}
            icon={TrendingUp}
            prefix="€"
          />
        </div>
      )}

      {/* Grafici principali */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Andamento Incassi */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Andamento Incassi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={andamento}>
                <defs>
                  <linearGradient id="colorIncasso" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="giorno" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `€${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="incasso"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#colorIncasso)"
                  name="Incasso"
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Servizi Top */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-emerald-600" />
              Servizi Più Richiesti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={serviziTop} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" fontSize={12} />
                <YAxis dataKey="nome" type="category" stroke="#64748b" fontSize={11} width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="count"
                  fill="#10b981"
                  radius={[0, 4, 4, 0]}
                  name="Prenotazioni"
                  animationDuration={1500}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Seconda riga grafici */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Distribuzione Età Clienti */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Clienti per Età
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={clientiEta}
                  dataKey="clienti"
                  nameKey="fascia"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ fascia, percent }) => `${fascia} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                  animationDuration={1500}
                >
                  {clientiEta.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {clientiEta.map((item, index) => (
                <div key={item.fascia} className="flex items-center gap-1 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                  <span>{item.fascia}: {item.clienti}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Orari di Punta */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Orari di Punta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={orariPunta}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="fascia" stroke="#64748b" fontSize={10} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="appuntamenti"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  name="Appuntamenti"
                  animationDuration={1500}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Scontrini e Appuntamenti */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-600" />
              Trend Giornaliero
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={andamento.slice(-14)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="giorno" stroke="#64748b" fontSize={10} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="scontrini"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  name="Scontrini"
                  animationDuration={1500}
                />
                <Line
                  type="monotone"
                  dataKey="appuntamenti"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981', strokeWidth: 2 }}
                  name="Appuntamenti"
                  animationDuration={1500}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Statistiche Clienti Nuovi */}
      {clientiNuovi && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cards Clienti Nuovi */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Clienti Nuovi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <p className="text-xs text-slate-500 mb-1">Questa Settimana</p>
                  <p className="text-3xl font-bold text-blue-600">{clientiNuovi.settimana.nuovi}</p>
                  <div className={`flex items-center justify-center gap-1 mt-1 text-xs ${
                    clientiNuovi.settimana.variazione >= 0 ? 'text-emerald-600' : 'text-red-500'
                  }`}>
                    {clientiNuovi.settimana.variazione >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {clientiNuovi.settimana.variazione >= 0 ? '+' : ''}{clientiNuovi.settimana.variazione}%
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Età media: {clientiNuovi.settimana.eta_media || '-'}</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-xl">
                  <p className="text-xs text-slate-500 mb-1">Questo Mese</p>
                  <p className="text-3xl font-bold text-emerald-600">{clientiNuovi.mese.nuovi}</p>
                  <div className={`flex items-center justify-center gap-1 mt-1 text-xs ${
                    clientiNuovi.mese.variazione >= 0 ? 'text-emerald-600' : 'text-red-500'
                  }`}>
                    {clientiNuovi.mese.variazione >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {clientiNuovi.mese.variazione >= 0 ? '+' : ''}{clientiNuovi.mese.variazione}%
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Età media: {clientiNuovi.mese.eta_media || '-'}</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <p className="text-xs text-slate-500 mb-1">Quest'Anno</p>
                  <p className="text-3xl font-bold text-purple-600">{clientiNuovi.anno.nuovi}</p>
                  <p className="text-xs text-slate-400 mt-3">Età media: {clientiNuovi.anno.eta_media || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trend Clienti Nuovi */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Trend Nuove Acquisizioni
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={trendClientiNuovi}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="inizio" stroke="#64748b" fontSize={10} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-2 rounded-lg shadow border text-xs">
                            <p className="font-medium">{payload[0].payload.inizio} - {payload[0].payload.fine}</p>
                            <p className="text-emerald-600">Nuovi clienti: {payload[0].value}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="nuovi_clienti"
                    fill="#10b981"
                    radius={[4, 4, 0, 0]}
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Statistiche per Genere */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fiche Media per Genere */}
        {ficheGenere && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-pink-600" />
                Fiche Media per Genere
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Donna */}
                <div className="flex items-center gap-4 p-4 bg-pink-50 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-pink-500 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Donna</p>
                    <p className="text-2xl font-bold text-pink-600">€{ficheGenere.donna.fiche_media}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Scontrini: {ficheGenere.donna.scontrini}</p>
                    <p className="text-sm font-medium text-slate-700">Tot: €{ficheGenere.donna.incasso_totale.toLocaleString('it-IT')}</p>
                    {ficheGenere.donna.rivendita > 0 && (
                      <p className="text-xs text-green-600">Rivendita: €{ficheGenere.donna.rivendita.toLocaleString('it-IT')}</p>
                    )}
                  </div>
                </div>
                
                {/* Uomo */}
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Uomo</p>
                    <p className="text-2xl font-bold text-blue-600">€{ficheGenere.uomo.fiche_media}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Scontrini: {ficheGenere.uomo.scontrini}</p>
                    <p className="text-sm font-medium text-slate-700">Tot: €{ficheGenere.uomo.incasso_totale.toLocaleString('it-IT')}</p>
                    {ficheGenere.uomo.rivendita > 0 && (
                      <p className="text-xs text-green-600">Rivendita: €{ficheGenere.uomo.rivendita.toLocaleString('it-IT')}</p>
                    )}
                  </div>
                </div>

                {/* Confronto visivo */}
                <div className="pt-3 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-slate-500">Distribuzione incasso</span>
                  </div>
                  <div className="h-4 bg-slate-100 rounded-full overflow-hidden flex">
                    {(() => {
                      const totale = ficheGenere.donna.incasso_totale + ficheGenere.uomo.incasso_totale + ficheGenere.non_specificato.incasso_totale;
                      const pctDonna = totale > 0 ? (ficheGenere.donna.incasso_totale / totale) * 100 : 0;
                      const pctUomo = totale > 0 ? (ficheGenere.uomo.incasso_totale / totale) * 100 : 0;
                      return (
                        <>
                          <div className="bg-pink-500 h-full" style={{ width: `${pctDonna}%` }}></div>
                          <div className="bg-blue-500 h-full" style={{ width: `${pctUomo}%` }}></div>
                          <div className="bg-slate-300 h-full flex-1"></div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-slate-500">
                    <span>Donna {((ficheGenere.donna.incasso_totale / (ficheGenere.donna.incasso_totale + ficheGenere.uomo.incasso_totale + ficheGenere.non_specificato.incasso_totale)) * 100 || 0).toFixed(0)}%</span>
                    <span>Uomo {((ficheGenere.uomo.incasso_totale / (ficheGenere.donna.incasso_totale + ficheGenere.uomo.incasso_totale + ficheGenere.non_specificato.incasso_totale)) * 100 || 0).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Servizi per Genere */}
        {serviziGenere && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-600" />
                Servizi per Categoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Tabs Genere */}
                <div className="grid grid-cols-3 gap-2">
                  {['donna', 'uomo', 'unisex'].map((cat) => (
                    <div 
                      key={cat}
                      className={`p-3 rounded-lg text-center ${
                        cat === 'donna' ? 'bg-pink-50' : cat === 'uomo' ? 'bg-blue-50' : 'bg-purple-50'
                      }`}
                    >
                      <p className={`text-xs font-medium ${
                        cat === 'donna' ? 'text-pink-600' : cat === 'uomo' ? 'text-blue-600' : 'text-purple-600'
                      }`}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </p>
                      <p className="text-xl font-bold text-slate-800">{serviziGenere[cat].count}</p>
                      <p className="text-xs text-slate-500">€{serviziGenere[cat].incasso.toLocaleString('it-IT')}</p>
                    </div>
                  ))}
                </div>
                
                {/* Top Servizi per categoria */}
                <div className="space-y-3">
                  {['donna', 'uomo'].map((cat) => (
                    <div key={cat}>
                      <p className={`text-xs font-medium mb-2 ${
                        cat === 'donna' ? 'text-pink-600' : 'text-blue-600'
                      }`}>
                        Top {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </p>
                      <div className="space-y-1">
                        {serviziGenere[cat].servizi.slice(0, 3).map((s) => (
                          <div key={`${cat}-${s.nome}`} className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 truncate">{s.nome}</span>
                            <span className="font-medium text-slate-800">{s.count}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Card Prepagate e Prodotti Venduti */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card Prepagate */}
        {cardStats && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                Card Prepagate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-emerald-50 rounded-xl">
                    <p className="text-2xl font-bold text-emerald-600">{cardStats.cards.attive}</p>
                    <p className="text-xs text-slate-500">Card Attive</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600">€{cardStats.cards.saldo_totale}</p>
                    <p className="text-xs text-slate-500">Saldo Totale</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-xl">
                    <p className="text-2xl font-bold text-purple-600">{cardStats.cards.totali}</p>
                    <p className="text-xs text-slate-500">Card Totali</p>
                  </div>
                </div>

                {/* Pagamenti e Ricariche */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">Pagamenti</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600">€{cardStats.pagamenti.totale}</p>
                    <p className="text-xs text-slate-500">{cardStats.pagamenti.numero} transazioni</p>
                    <p className="text-xs text-slate-400">Media: €{cardStats.pagamenti.media}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">Ricariche</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">€{cardStats.ricariche.totale}</p>
                    <p className="text-xs text-slate-500">{cardStats.ricariche.numero} ricariche</p>
                    <p className="text-xs text-slate-400">Media: €{cardStats.ricariche.media}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Prodotti Venduti (Rivendita) */}
        {prodottiStats && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-amber-600" />
                Prodotti Venduti (Rivendita)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Stats principali */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-amber-50 rounded-xl">
                    <p className="text-2xl font-bold text-amber-600">€{prodottiStats.totale_incasso}</p>
                    <p className="text-xs text-slate-500">Incasso Totale</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-xl">
                    <p className="text-2xl font-bold text-orange-600">{prodottiStats.totale_pezzi}</p>
                    <p className="text-xs text-slate-500">Pezzi Venduti</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-xl">
                    <p className="text-2xl font-bold text-yellow-600">€{prodottiStats.media_prezzo}</p>
                    <p className="text-xs text-slate-500">Prezzo Medio</p>
                  </div>
                </div>

                {/* Top Prodotti */}
                {prodottiStats.top_prodotti && prodottiStats.top_prodotti.length > 0 ? (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Top Prodotti Venduti</p>
                    <div className="space-y-2">
                      {prodottiStats.top_prodotti.map((p) => (
                        <div key={`prodotto-${p.nome}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-700">{p.nome}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-slate-800">{p.quantita}x</span>
                            <span className="text-xs text-slate-500 ml-2">€{p.incasso}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nessuna vendita prodotti nel periodo</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Promozioni */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-pink-600" />
              Promozioni Attive
            </CardTitle>
            <Button variant="outline" size="sm" className="gap-2">
              <Plus className="w-4 h-4" />
              Nuova Promo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {promozioni.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Tag className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessuna promozione attiva</p>
              <p className="text-sm">Crea la tua prima promozione per attrarre nuovi clienti!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {promozioni.map((promo) => {
                const oggi = new Date().toISOString().split('T')[0];
                const isActive = promo.attiva && promo.data_inizio <= oggi && promo.data_fine >= oggi;
                
                return (
                  <div
                    key={promo.id}
                    className={`p-4 rounded-lg border-2 ${
                      isActive ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-slate-800">{promo.nome}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        isActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {isActive ? 'Attiva' : 'Scaduta'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-3">{promo.descrizione}</p>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl font-bold text-blue-600">
                        {promo.tipo_sconto === 'percentuale' ? `-${promo.valore_sconto}%` : `-€${promo.valore_sconto}`}
                      </span>
                    </div>
                    
                    <div className="space-y-1 text-xs text-slate-500">
                      <p>Valida: {format(new Date(promo.data_inizio), "dd/MM/yyyy", { locale: it })} - {format(new Date(promo.data_fine), "dd/MM/yyyy", { locale: it })}</p>
                      <div className="flex justify-between pt-2 border-t border-slate-200 mt-2">
                        <span>Utilizzi: <strong className="text-slate-700">{promo.utilizzi}</strong></span>
                        <span>Incasso: <strong className="text-emerald-600">€{promo.incasso_generato.toLocaleString('it-IT')}</strong></span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
          </div>
        </TabsContent>

        {/* TAB: Report Operatori */}
        <TabsContent value="operatori">
          <ReportOperatori variant="full" />
        </TabsContent>

        {/* TAB: Marketing */}
        <TabsContent value="marketing" data-testid="marketing-tab-content">
          <div className="space-y-6">
            {loadingMarketing ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* KPI Marketing */}
                {marketingDashboard && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="marketing-kpi-grid">
                    <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white" data-testid="kpi-totale-clienti">
                      <CardContent className="p-4 text-center">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
                        <p className="text-3xl font-bold">{marketingDashboard.kpi.totale_clienti}</p>
                        <p className="text-sm opacity-80">Totale Clienti</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white" data-testid="kpi-clienti-attivi">
                      <CardContent className="p-4 text-center">
                        <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-80" />
                        <p className="text-3xl font-bold">{marketingDashboard.kpi.clienti_attivi_30g}</p>
                        <p className="text-sm opacity-80">Attivi (&lt;30g)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white" data-testid="kpi-da-richiamare">
                      <CardContent className="p-4 text-center">
                        <Phone className="w-8 h-8 mx-auto mb-2 opacity-80" />
                        <p className="text-3xl font-bold">{marketingDashboard.kpi.clienti_da_richiamare_60g}</p>
                        <p className="text-sm opacity-80">Da Richiamare</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white" data-testid="kpi-a-rischio">
                      <CardContent className="p-4 text-center">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-80" />
                        <p className="text-3xl font-bold">{marketingDashboard.kpi.clienti_a_rischio_90g}</p>
                        <p className="text-sm opacity-80">A Rischio</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white" data-testid="kpi-persi">
                      <CardContent className="p-4 text-center">
                        <XCircle className="w-8 h-8 mx-auto mb-2 opacity-80" />
                        <p className="text-3xl font-bold">{marketingDashboard.kpi.clienti_persi}</p>
                        <p className="text-sm opacity-80">Persi (&gt;90g)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-slate-500 to-slate-600 text-white" data-testid="kpi-mai-venuti">
                      <CardContent className="p-4 text-center">
                        <UserX className="w-8 h-8 mx-auto mb-2 opacity-80" />
                        <p className="text-3xl font-bold">{marketingDashboard.kpi.clienti_mai_venuti}</p>
                        <p className="text-sm opacity-80">Mai Venuti</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Sezione Clienti Nuovi con Canale Acquisizione */}
                {clientiNuoviMarketing && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="clienti-nuovi-marketing">
                    {/* Card Riepilogo Clienti Nuovi */}
                    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                          Clienti Nuovi del Mese
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-end justify-between">
                          <div>
                            <p className="text-4xl font-bold text-amber-600">{clientiNuoviMarketing.totale_clienti_nuovi}</p>
                            <p className="text-sm text-slate-500">questo mese</p>
                          </div>
                          <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                            clientiNuoviMarketing.trend === 'up' ? 'bg-green-100 text-green-700' :
                            clientiNuoviMarketing.trend === 'down' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {clientiNuoviMarketing.trend === 'up' ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : clientiNuoviMarketing.trend === 'down' ? (
                              <TrendingDown className="w-4 h-4" />
                            ) : null}
                            {clientiNuoviMarketing.variazione_percentuale > 0 ? '+' : ''}{clientiNuoviMarketing.variazione_percentuale}%
                          </div>
                        </div>
                        <div className="text-sm text-slate-500">
                          vs {clientiNuoviMarketing.totale_mese_precedente} mese precedente
                        </div>
                        
                        {/* Tasso di Ritorno */}
                        <div className="mt-4 p-3 bg-white rounded-lg border border-amber-100">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Tasso di Ritorno</span>
                            <span className="text-lg font-bold text-blue-600">{clientiNuoviMarketing.tasso_ritorno?.valore || 0}%</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {clientiNuoviMarketing.tasso_ritorno?.clienti_tornati || 0} su {clientiNuoviMarketing.tasso_ritorno?.totale_clienti_nuovi || 0} sono tornati
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Card Canali di Acquisizione */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-500" />
                          Come ci hanno conosciuto
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {clientiNuoviMarketing.canali_acquisizione?.length > 0 ? (
                          <div className="space-y-3">
                            {clientiNuoviMarketing.canali_acquisizione.map((canale, idx) => (
                              <div key={canale.canale} className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  canale.canale === 'passaparola' ? 'bg-blue-100 text-blue-600' :
                                  canale.canale === 'social' ? 'bg-pink-100 text-pink-600' :
                                  canale.canale === 'google' ? 'bg-green-100 text-green-600' :
                                  canale.canale === 'volantino' ? 'bg-orange-100 text-orange-600' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {canale.canale === 'passaparola' && <Users className="w-4 h-4" />}
                                  {canale.canale === 'social' && <Share2 className="w-4 h-4" />}
                                  {canale.canale === 'google' && <Search className="w-4 h-4" />}
                                  {canale.canale === 'volantino' && <FileText className="w-4 h-4" />}
                                  {canale.canale === 'altro' && <HelpCircle className="w-4 h-4" />}
                                  {canale.canale === 'non_specificato' && <HelpCircle className="w-4 h-4" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium">{canale.label}</span>
                                    <span className="text-sm text-slate-500">{canale.count} ({canale.percentuale}%)</span>
                                  </div>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${
                                        canale.canale === 'passaparola' ? 'bg-blue-500' :
                                        canale.canale === 'social' ? 'bg-pink-500' :
                                        canale.canale === 'google' ? 'bg-green-500' :
                                        canale.canale === 'volantino' ? 'bg-orange-500' :
                                        'bg-slate-400'
                                      }`}
                                      style={{ width: `${canale.percentuale}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-400">
                            <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Nessun cliente nuovo questo mese</p>
                            <p className="text-xs mt-1">Segna i clienti nuovi nell'agenda con la stellina</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Metriche */}
                {marketingDashboard && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-green-100 rounded-xl">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">{marketingDashboard.metriche.tasso_fidelizzazione}%</p>
                            <p className="text-sm text-slate-500">Tasso Fidelizzazione</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-red-100 rounded-xl">
                            <TrendingDown className="w-6 h-6 text-red-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-red-600">{marketingDashboard.metriche.tasso_rischio}%</p>
                            <p className="text-sm text-slate-500">Tasso Rischio</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-blue-100 rounded-xl">
                            <Calendar className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-blue-600">{marketingDashboard.metriche.frequenza_media_visite}</p>
                            <p className="text-sm text-slate-500">Visite Medie/Cliente</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Filtro e Lista Clienti da Richiamare */}
                <Card data-testid="clienti-da-richiamare-card">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="w-5 h-5 text-yellow-500" />
                      Clienti da Richiamare
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Assenti da più di:</span>
                      <Select value={giorniAssenza.toString()} onValueChange={(v) => { setGiorniAssenza(parseInt(v)); fetchMarketingData(); }}>
                        <SelectTrigger className="w-28" data-testid="giorni-assenza-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 giorni</SelectItem>
                          <SelectItem value="30">30 giorni</SelectItem>
                          <SelectItem value="45">45 giorni</SelectItem>
                          <SelectItem value="60">60 giorni</SelectItem>
                          <SelectItem value="90">90 giorni</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {clientiDaRichiamare.length === 0 ? (
                      <div className="text-center py-8 text-slate-500" data-testid="no-clienti-da-richiamare">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                        <p>Nessun cliente da richiamare!</p>
                        <p className="text-sm">Tutti i clienti sono stati visti di recente.</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="clienti-da-richiamare-list">
                        {clientiDaRichiamare.map((cliente) => (
                          <div 
                            key={cliente.cliente_id}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer hover:bg-slate-50 ${
                              cliente.priorita === 'alta' ? 'border-red-200 bg-red-50' :
                              cliente.priorita === 'media' ? 'border-orange-200 bg-orange-50' :
                              'border-slate-200'
                            }`}
                            onClick={() => fetchAnalisiCliente(cliente.cliente_id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                cliente.priorita === 'alta' ? 'bg-red-500' :
                                cliente.priorita === 'media' ? 'bg-orange-500' :
                                'bg-blue-500'
                              }`}>
                                {cliente.nome?.charAt(0)}{cliente.cognome?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{cliente.nome} {cliente.cognome}</p>
                                <div className="flex items-center gap-3 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {cliente.giorni_assenza} giorni
                                  </span>
                                  {cliente.tempo_medio_ritorno > 0 && (
                                    <span className="text-xs">
                                      (media: {cliente.tempo_medio_ritorno}g)
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Receipt className="w-3 h-3" />
                                    {cliente.num_visite} visite
                                  </span>
                                </div>
                                {cliente.servizi_preferiti?.length > 0 && (
                                  <div className="flex gap-1 mt-1">
                                    {cliente.servizi_preferiti.map((s, i) => (
                                      <span key={`${cliente.id}-servizio-${i}-${s}`} className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                        {s}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-600">
                                €{cliente.incasso_totale}
                              </span>
                              {cliente.telefono && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); window.open(`tel:${cliente.telefono}`); }}
                                  className="gap-1"
                                >
                                  <Phone className="w-3 h-3" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); handleImpostaReminder(cliente); }}
                                className="gap-1"
                                title="Imposta reminder"
                              >
                                <Bell className="w-3 h-3" />
                              </Button>
                              {cliente.email && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); handleInviaRichiamo(cliente); }}
                                  className="gap-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                  title="Invia email di richiamo"
                                >
                                  <Mail className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Dettaglio Cliente Selezionato */}
                {clienteAnalisi && (
                  <Card className="border-2 border-blue-200" data-testid="cliente-analisi-card">
                    <CardHeader className="flex flex-row items-center justify-between bg-blue-50">
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-blue-500" />
                        Analisi Cliente: {clienteAnalisi.cliente.nome} {clienteAnalisi.cliente.cognome}
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setClienteAnalisi(null)} data-testid="close-cliente-analisi">
                        ✕
                      </Button>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">Visite Totali</p>
                          <p className="text-2xl font-bold">{clienteAnalisi.statistiche.num_visite}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">Spesa Totale</p>
                          <p className="text-2xl font-bold">€{clienteAnalisi.statistiche.incasso_totale}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">Fiche Media</p>
                          <p className="text-2xl font-bold">€{clienteAnalisi.statistiche.fiche_media}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">Frequenza Ritorno</p>
                          <p className="text-2xl font-bold">{clienteAnalisi.statistiche.tempo_medio_ritorno || '-'} giorni</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium mb-2">Servizi Preferiti</h4>
                          <div className="space-y-1">
                            {clienteAnalisi.servizi_preferiti?.map((s, i) => (
                              <div key={`servizio-pref-${i}-${s.nome}`} className="flex justify-between text-sm p-2 bg-slate-50 rounded">
                                <span>{s.nome}</span>
                                <span className="text-slate-500">{s.count}x - €{s.incasso}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Suggerimenti</h4>
                          <div className="space-y-2">
                            {clienteAnalisi.suggerimenti?.map((s, i) => (
                              <div key={`suggerimento-${i}-${s.priorita}`} className={`p-2 rounded text-sm ${
                                s.priorita === 'alta' ? 'bg-red-50 border-l-4 border-red-500' :
                                s.priorita === 'media' ? 'bg-yellow-50 border-l-4 border-yellow-500' :
                                'bg-blue-50 border-l-4 border-blue-500'
                              }`}>
                                {s.messaggio}
                              </div>
                            ))}
                            {clienteAnalisi.statistiche.prossimo_appuntamento_suggerito && (
                              <div className="p-2 bg-green-50 border-l-4 border-green-500 text-sm">
                                <strong>Prossimo appuntamento suggerito:</strong> {clienteAnalisi.statistiche.prossimo_appuntamento_suggerito}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Sezione Profilazione Capelli */}
                {profilazioneStats && profilazioneStats.totale_profilati > 0 && (
                  <Card className="border-amber-200" data-testid="profilazione-stats-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        Profilazione Capelli Clienti
                        <span className="text-sm font-normal text-slate-500 ml-2">
                          ({profilazioneStats.totale_profilati} su {profilazioneStats.totale_clienti} clienti profilati - {profilazioneStats.percentuale_profilazione}%)
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Colore Naturale */}
                        {profilazioneStats.statistiche.colore_naturale?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                              Colore Naturale
                            </h4>
                            <div className="space-y-2">
                              {profilazioneStats.statistiche.colore_naturale.map(item => (
                                <div key={item.valore} className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm mb-1">
                                      <span>{item.label}</span>
                                      <span className="text-slate-500">{item.count} ({item.percentuale}%)</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-amber-500 rounded-full"
                                        style={{ width: `${item.percentuale}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Percentuale Bianchi */}
                        {profilazioneStats.statistiche.percentuale_bianchi?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                              % Capelli Bianchi
                            </h4>
                            <div className="space-y-2">
                              {profilazioneStats.statistiche.percentuale_bianchi.map(item => (
                                <div key={item.valore} className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm mb-1">
                                      <span>{item.label}</span>
                                      <span className="text-slate-500">{item.count} ({item.percentuale}%)</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-slate-500 rounded-full"
                                        style={{ width: `${item.percentuale}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tipo Capello */}
                        {profilazioneStats.statistiche.tipo_capello?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                              Tipo Capello
                            </h4>
                            <div className="space-y-2">
                              {profilazioneStats.statistiche.tipo_capello.map(item => (
                                <div key={item.valore} className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm mb-1">
                                      <span>{item.label}</span>
                                      <span className="text-slate-500">{item.count} ({item.percentuale}%)</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-blue-500 rounded-full"
                                        style={{ width: `${item.percentuale}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Stato Capello */}
                        {profilazioneStats.statistiche.stato_capello?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-red-500"></div>
                              Stato Capello
                            </h4>
                            <div className="space-y-2">
                              {profilazioneStats.statistiche.stato_capello.map(item => (
                                <div key={item.valore} className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm mb-1">
                                      <span>{item.label}</span>
                                      <span className="text-slate-500">{item.count} ({item.percentuale}%)</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-red-500 rounded-full"
                                        style={{ width: `${item.percentuale}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Trattamenti Chimici */}
                        {profilazioneStats.statistiche.trattamenti_chimici?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                              Trattamenti Chimici
                            </h4>
                            <div className="space-y-2">
                              {profilazioneStats.statistiche.trattamenti_chimici.map(item => (
                                <div key={item.valore} className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm mb-1">
                                      <span>{item.label}</span>
                                      <span className="text-slate-500">{item.count} ({item.percentuale}%)</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-orange-500 rounded-full"
                                        style={{ width: `${item.percentuale}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Colore Attuale */}
                        {profilazioneStats.statistiche.colore_attuale?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                              Colore Attuale
                            </h4>
                            <div className="space-y-2">
                              {profilazioneStats.statistiche.colore_attuale.map(item => (
                                <div key={item.valore} className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm mb-1">
                                      <span>{item.label}</span>
                                      <span className="text-slate-500">{item.count} ({item.percentuale}%)</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-purple-500 rounded-full"
                                        style={{ width: `${item.percentuale}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
